import {
	DOM_NPS_TAGNAME,
	FloodgateStringProviderStringType,
	IFloodgateStringProvider,
	INpsSurvey,
	ISurveyComponent,
	ISurveyInfo,
	IUxSchema,
	JSON_APPLICATION_KEYNAME,
	JSON_COMMENT_KEYNAME,
	JSON_EXTENDEDMANIFESTDATA_KEYNAME,
	JSON_PROMPT_KEYNAME,
	JSON_RATING_KEYNAME,
	JSON_SURVEYSTRINGS_KEYNAME,
	SurveyComponentType,
	SurveyType,
} from "@ms-ofb/floodgate-shared-typings";
import { CampaignSurveyContent, CampaignSurveyTemplate } from "../Campaign/CampaignDefinitionProvider";
import CommentComponent from "../SurveyComponents/CommentComponent";
import CVSurveyComponent from "../SurveyComponents/CVSurveyComponent";
import PromptComponent from "../SurveyComponents/PromptComponent";
import RatingComponent from "../SurveyComponents/RatingComponent";
import * as Utils from "../Utils";
import { Survey } from "./Survey";
import SurveyDataSource from "./SurveyDataSource";

const { isNOU, overrideValues } = Utils;

class NpsSurvey extends Survey implements INpsSurvey {
	public static make(data: NpsSurvey.NpsSurveyData): INpsSurvey {
		try {
			return new NpsSurvey(data);
		} catch (e) {
			return null;
		}
	}

	public static makeCustom(baseData: SurveyDataSource.SurveyDataSourceData, sp: IFloodgateStringProvider,
		surveyModel: CampaignSurveyTemplate): INpsSurvey {

		if (isNOU(baseData) || isNOU(sp) || isNOU(surveyModel)) {
			return null;
		}

		const ratingValuesAscending: string[] = [];
		const content: CampaignSurveyContent = surveyModel.content;
		let contentValid = true;

		// Either UxSchema or Prompt/Content must be in the survey
		if (isNOU(content) || isNOU(content.comment)
			|| isNOU(content.prompt) || isNOU(content.rating)) {
			contentValid = false;
			if (!surveyModel.uxSchema) {
				return null
			}
		}

		const data = new NpsSurvey.NpsSurveyData();
		data.baseData = baseData;
		data.uxSchema = surveyModel.uxSchema;

		if (contentValid) {
			data.promptData = new PromptComponent.PromptComponentData();
			data.ratingData = new RatingComponent.RatingComponentData();
			data.commentData = new CommentComponent.CommentComponentData();

			data.promptData.title = sp.getCustomString(content.prompt.title);
			data.promptData.question = sp.getCustomString(content.prompt.question);
			data.promptData.yesButtonLabel = sp.getCustomString(content.prompt.yesLabel);
			data.promptData.noButtonLabel = sp.getCustomString(content.prompt.noLabel);
			data.ratingData.question = sp.getCustomString(content.rating.question);
			data.ratingData.isZeroBased = content.rating.isZeroBased;
			data.commentData.question = sp.getCustomString(content.comment.question);

			for (const value of content.rating.ratingValuesAscending) {
				const customString: string = sp.getCustomString(value);

				if (isNOU(customString)) {
					return null;
				}

				ratingValuesAscending.push(customString);
			}

			data.ratingData.ratingValuesAscending = ratingValuesAscending;

			if (isNOU(data.ratingData.question)
				|| isNOU(data.commentData.question)
				|| isNOU(data.promptData.title)
				|| isNOU(data.promptData.question)
				|| isNOU(data.promptData.yesButtonLabel)
				|| isNOU(data.promptData.noButtonLabel)
				|| isNOU(data.ratingData.ratingValuesAscending)) {

				return null;
			}

			// cvsurvey data is not required on all surveys.
			if (content.cvSurvey) {
				data.cvSurveyData = new CVSurveyComponent.CVSurveyComponentData();
				data.cvSurveyData.customerVoiceFormId = content.cvSurvey.customerVoiceFormId;

				if (isNOU(data.cvSurveyData.customerVoiceFormId)) {
					return null;
				}
			}
		}

		return this.make(data);
	}

	public static make5Point(baseData: SurveyDataSource.SurveyDataSourceData, sp: IFloodgateStringProvider): INpsSurvey {
		if (!baseData || !sp) {
			return null;
		}

		const data = new NpsSurvey.NpsSurveyData();
		data.baseData = baseData;
		data.ratingData = new RatingComponent.RatingComponentData();
		data.ratingData.isZeroBased = false;
		data.commentData = new CommentComponent.CommentComponentData();
		data.promptData = new PromptComponent.PromptComponentData();
		data.ratingData.question = sp.loadStringResource(FloodgateStringProviderStringType.NpsRatingQuestion);
		data.commentData.question = sp.loadStringResource(FloodgateStringProviderStringType.NpsCommentQuestion);
		data.promptData.title = sp.loadStringResource(FloodgateStringProviderStringType.NpsPromptTitle);
		data.promptData.question = sp.loadStringResource(FloodgateStringProviderStringType.NpsPromptQuestion);
		data.promptData.yesButtonLabel = sp.loadStringResource(FloodgateStringProviderStringType.NpsPromptYesLabel);
		data.promptData.noButtonLabel = sp.loadStringResource(FloodgateStringProviderStringType.NpsPromptNotNowLabel);
		const ratingValuesAscending: string[] = new Array(5);
		ratingValuesAscending[0] = sp.loadStringResource(FloodgateStringProviderStringType.Nps5RatingValue1);
		ratingValuesAscending[1] = sp.loadStringResource(FloodgateStringProviderStringType.Nps5RatingValue2);
		ratingValuesAscending[2] = sp.loadStringResource(FloodgateStringProviderStringType.Nps5RatingValue3);
		ratingValuesAscending[3] = sp.loadStringResource(FloodgateStringProviderStringType.Nps5RatingValue4);
		ratingValuesAscending[4] = sp.loadStringResource(FloodgateStringProviderStringType.Nps5RatingValue5);

		if (!data.ratingData.question
			|| !data.commentData.question
			|| !data.promptData.title
			|| !data.promptData.question
			|| !data.promptData.yesButtonLabel
			|| !data.promptData.noButtonLabel
			|| !ratingValuesAscending[0]
			|| !ratingValuesAscending[1]
			|| !ratingValuesAscending[2]
			|| !ratingValuesAscending[3]
			|| !ratingValuesAscending[4]) {

			return null;
		}

		data.ratingData.ratingValuesAscending = ratingValuesAscending;
		return this.make(data);
	}

	public static make11Point(baseData: SurveyDataSource.SurveyDataSourceData, sp: IFloodgateStringProvider): INpsSurvey {
		if (!baseData || !sp) {
			return null;
		}

		const data = new NpsSurvey.NpsSurveyData();
		data.baseData = baseData;
		data.ratingData = new RatingComponent.RatingComponentData();
		data.ratingData.isZeroBased = true;
		data.commentData = new CommentComponent.CommentComponentData();
		data.promptData = new PromptComponent.PromptComponentData();
		data.ratingData.question = sp.loadStringResource(FloodgateStringProviderStringType.NpsRatingQuestion);
		data.commentData.question = sp.loadStringResource(FloodgateStringProviderStringType.NpsCommentQuestion);
		data.promptData.title = sp.loadStringResource(FloodgateStringProviderStringType.NpsPromptTitle);
		data.promptData.question = sp.loadStringResource(FloodgateStringProviderStringType.NpsPromptQuestion);
		data.promptData.yesButtonLabel = sp.loadStringResource(FloodgateStringProviderStringType.NpsPromptYesLabel);
		data.promptData.noButtonLabel = sp.loadStringResource(FloodgateStringProviderStringType.NpsPromptNotNowLabel);

		const ratingValuesAscending: string[] = new Array(11);
		ratingValuesAscending[0] = sp.loadStringResource(FloodgateStringProviderStringType.Nps11RatingValue0);
		ratingValuesAscending[1] = sp.loadStringResource(FloodgateStringProviderStringType.Nps11RatingValue1);
		ratingValuesAscending[2] = sp.loadStringResource(FloodgateStringProviderStringType.Nps11RatingValue2);
		ratingValuesAscending[3] = sp.loadStringResource(FloodgateStringProviderStringType.Nps11RatingValue3);
		ratingValuesAscending[4] = sp.loadStringResource(FloodgateStringProviderStringType.Nps11RatingValue4);
		ratingValuesAscending[5] = sp.loadStringResource(FloodgateStringProviderStringType.Nps11RatingValue5);
		ratingValuesAscending[6] = sp.loadStringResource(FloodgateStringProviderStringType.Nps11RatingValue6);
		ratingValuesAscending[7] = sp.loadStringResource(FloodgateStringProviderStringType.Nps11RatingValue7);
		ratingValuesAscending[8] = sp.loadStringResource(FloodgateStringProviderStringType.Nps11RatingValue8);
		ratingValuesAscending[9] = sp.loadStringResource(FloodgateStringProviderStringType.Nps11RatingValue9);
		ratingValuesAscending[10] = sp.loadStringResource(FloodgateStringProviderStringType.Nps11RatingValue10);

		if (!data.ratingData.question
			|| !data.commentData.question
			|| !data.promptData.title
			|| !data.promptData.question
			|| !data.promptData.yesButtonLabel
			|| !data.promptData.noButtonLabel
			|| !ratingValuesAscending[0]
			|| !ratingValuesAscending[1]
			|| !ratingValuesAscending[2]
			|| !ratingValuesAscending[3]
			|| !ratingValuesAscending[4]
			|| !ratingValuesAscending[5]
			|| !ratingValuesAscending[6]
			|| !ratingValuesAscending[7]
			|| !ratingValuesAscending[8]
			|| !ratingValuesAscending[9]
			|| !ratingValuesAscending[10]) {

			return null;
		}

		data.ratingData.ratingValuesAscending = ratingValuesAscending;
		return this.make(data);
	}

	private surveyInfo: SurveyDataSource;
	private question: CommentComponent;
	private prompt: PromptComponent;
	private rating: RatingComponent;
	private cvSurvey: CVSurveyComponent;
	private contentValid: boolean;

	private constructor(data: NpsSurvey.NpsSurveyData) {
		super();
		if (!data) {
			throw new Error("data must not be null");
		}

		if (data.uxSchema) {
			this.setUxSchema(data.uxSchema);
		}

		this.contentValid = (!Utils.isNOU(data.promptData?.question) && !Utils.isNOU(data.promptData?.title)) &&
		!Utils.isNOU(data.ratingData?.question) && !Utils.isNOU(data.ratingData?.ratingValuesAscending) &&
		!Utils.isNOU(data.commentData?.question);

		this.surveyInfo = new SurveyDataSource(data.baseData);
		if (this.contentValid) {
			this.prompt = new PromptComponent(data.promptData);
			this.question = new CommentComponent(data.commentData);
			this.rating = new RatingComponent(data.ratingData);
			this.cvSurvey = data.cvSurveyData ? (new CVSurveyComponent(data.cvSurveyData)) : null;
		}
	}

	// @Override
	public getType(): SurveyType {
		return SurveyType.Nps;
	}

	// @Override
	public getSurveyInfo(): ISurveyInfo {
		return this.surveyInfo;
	}

	// @Override
	public getCommentComponent(): CommentComponent {
		return this.question;
	}

	// @Override
	public getPromptComponent(): PromptComponent {
		return this.prompt;
	}

	// @Override
	public getRatingComponent(): RatingComponent {
		return this.rating;
	}

	// @Override
	public getCVSurveyComponent(): CVSurveyComponent {
		return this.cvSurvey;
	}

	// @Override
	public getTags(): string[] {
		return this.surveyInfo.getTags();
	}

	// @Override
	public getComponent(componentType: SurveyComponentType): ISurveyComponent {
		switch (componentType) {
			case SurveyComponentType.Comment:
				return this.getCommentComponent();
			case SurveyComponentType.Prompt:
				return this.getPromptComponent();
			case SurveyComponentType.Rating:
				return this.getRatingComponent();
			case SurveyComponentType.CVSurvey:
				return this.getCVSurveyComponent();
			default:
				return null;
		}
	}

	// @Override
	public getDomElements(doc: Document): Element[] {
		if (!doc) {
			throw new Error("Document must not be null");
		}

		const element: Element = doc.createElement(DOM_NPS_TAGNAME);

		this.getSurveyInfo().getDomElements(doc).forEach((child) => {
			if (child) {
				element.appendChild(child);
			}
		});

		this.getCommentComponent().getDomElements(doc).forEach((child) => {
			if (child) {
				element.appendChild(child);
			}
		});

		this.getRatingComponent().getDomElements(doc).forEach((child) => {
			if (child) {
				element.appendChild(child);
			}
		});

		return [element];
	}

	// @Override
	public getJsonElements(): object {
		let result: object = {};

		result = overrideValues(this.getSurveyInfo().getJsonElements(), result);
		result = overrideValues(this.getCommentComponent().getJsonElements(), result);
		result = overrideValues(this.getRatingComponent().getJsonElements(), result);

		result[JSON_APPLICATION_KEYNAME] = {};
		result[JSON_APPLICATION_KEYNAME][JSON_EXTENDEDMANIFESTDATA_KEYNAME] =
			JSON.stringify({ [JSON_SURVEYSTRINGS_KEYNAME]: this.makeSurveyStrings() });

		return result;
	}
	
	// @Override
	public isContentValid(): boolean {
		return this.contentValid;
	}

	// Makes the string to send to Manifest respecting the components present in the survey
	private makeSurveyStrings(): string {
		const actualStrings: object = {};

		if (this.prompt) {
			actualStrings[JSON_PROMPT_KEYNAME] = this.prompt.getComponentJson();
		}

		if (this.rating) {
			actualStrings[JSON_RATING_KEYNAME] = [this.rating.getComponentJson()];
		}

		if (this.question) {
			actualStrings[JSON_COMMENT_KEYNAME] = [this.question.getComponentJson()];
		}

		return JSON.stringify(actualStrings);
	}
}

module NpsSurvey {
	/**
	 * Data required for a Nps Survey
	 */
	export class NpsSurveyData {
		public baseData: SurveyDataSource.SurveyDataSourceData;
		public commentData: CommentComponent.CommentComponentData;
		public promptData: PromptComponent.PromptComponentData;
		public ratingData: RatingComponent.RatingComponentData;
		public cvSurveyData: CVSurveyComponent.CVSurveyComponentData;
		public uxSchema: IUxSchema;
	}
}

export default NpsSurvey;
